
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { pedidoGeralDto } from './fetchAllPedidosGeral';
import Chart from 'chart.js';

const formatCurrency = (val: number) => `R$ ${(val / 100).toFixed(2).replace('.', ',')}`;

export const gerarGraficoPizza = async (
  dados: pedidoGeralDto[],
  doc: jsPDF
): Promise<void> => {
  const pedidosPorCliente: Record<string, number> = {};
  dados.forEach((p) => {
    const nome = p.cliente || 'Desconhecido';
    pedidosPorCliente[nome] = (pedidosPorCliente[nome] || 0) + 1;
  });

  const labels: string[] = Object.keys(pedidosPorCliente);
  const valores: number[] = Object.values(pedidosPorCliente);

  const canvas = document.createElement('canvas');
  canvas.width = 400;
  canvas.height = 250;
  document.body.appendChild(canvas);

  const ctx = canvas.getContext('2d');

  new Chart(ctx!, {
    type: 'pie',
    data: {
      labels,
      datasets: [
        {
          data: valores,
          backgroundColor: [
            '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0',
            '#9966FF', '#FF9F40', '#00C853', '#FF5722',
            '#3F51B5', '#E91E63', '#00BCD4', '#FFC107'
          ],
        },
      ],
    },
    options: {
      layout: {
        padding: {
          top: 0,
          bottom: 0
        }
      },
      legend: {
        display: true,
        position: 'right',
        labels: {
          fontSize: 30,
          generateLabels(chart: any) {
            const data = chart.data;
            if (!data.labels || !data.datasets.length) return [];

            const total = data.datasets[0].data.reduce(
              (sum: number, val: number) => sum + Number(val),
              0
            );

            return data.labels.map((label: string, i: number) => {
              const value = data.datasets[0].data[i];
              const percentage = ((value / total) * 100).toFixed(1);
              return {
                text: `${label} (${value} pedidos - ${percentage}%)`,
                fillStyle: data.datasets[0].backgroundColor[i],
                hidden: false,
                index: i,
              };
            });
          },
        },
      },
    },
  });

  await new Promise((res) => setTimeout(res, 1000));
  const image = await html2canvas(canvas, { scale: 2 });
  const imgData = image.toDataURL('image/png');

  const originalWidth = canvas.width;
  const originalHeight = canvas.height;

  const desiredWidth = 180;
  const ratio = originalHeight / originalWidth;
  const desiredHeight = desiredWidth * ratio;

  document.body.removeChild(canvas);

  doc.addPage();
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("Gráfico de Pedidos por Cliente", 10, 20);
  doc.addImage(imgData, 'PNG', 10, 30, desiredWidth, desiredHeight);
};

export const gerarPDF = async (dados: pedidoGeralDto[]) => {
  const doc = new jsPDF();
  let totalGeral = 0;

  const pedidosPorCliente = dados.reduce((acc: { [key: string]: any[] }, pedido) => {
    const key = pedido.cliente?.toString() || 'Sem Cliente';
    if (!acc[key]) acc[key] = [];
    acc[key].push(pedido);
    return acc;
  }, {});

  let y = 15;
  Object.entries(pedidosPorCliente).forEach(([cliente, pedidos], index) => {
    if (index > 0) {
      doc.addPage();
      y = 9;
    }
    let totalCliente = 0;

    doc.setFontSize(14);
    const pageWidth = doc.internal.pageSize.getWidth();
    const textWidth = doc.getTextWidth(cliente);
    const centerX = (pageWidth - textWidth) / 2;
    doc.text(cliente, centerX, y);

    y += 8;

    pedidos.forEach((pedido: any) => {
      const pedidoData = new Date(pedido.created_at);
      const pedidoInfo = JSON.parse(pedido.pedido);

      const consumo = pedidoInfo.products.reduce((soma: number, p: any) => soma + p.price, 0);
      const taxaEntrega = pedidoInfo.deliveryPrice || 0;
      const desconto = pedidoInfo.discount || 0;
      const totalPedido = consumo + taxaEntrega - desconto;
      const valorPago = pedidoInfo.paymentPrice || totalPedido;

      let  pagamento = "-";
      switch (pedidoInfo.payment) {
        case "money": pagamento = "Dinheiro"; break;
        case "card": pagamento = "Cartão"; break;
        case "pix":  pagamento = "PIX"; break;
      }

      totalCliente += totalPedido;
      totalGeral += totalPedido;

      doc.setFontSize(10);
      doc.setFillColor(240,240,240);
      doc.rect(10, y - 5, pageWidth - 25, 7, 'F');
      doc.text(`${pedido.pessoa || "Cliente não informado"} | ${pedidoData.toLocaleString()}`, 12, y);
      y += 6;

      doc.setFontSize(9);
      doc.text(`Total Consumo: ${formatCurrency(consumo)}`, 14, y);
      y += 5
      doc.text(`Taxa de Entrega: ${formatCurrency(taxaEntrega)}`, 14, y);
      y += 3

      autoTable(doc, {
        startY: y,
        head: [["Produto", "Qtd", "Valor (R$)"]],
        body: pedidoInfo.products.map((prod: any) => [
          prod.product?.item?.nome || "Sem nome",
          prod.quantity || 1,
          formatCurrency(prod.price)
        ]),
        theme: 'grid',
        styles: { fontSize: 9 },
        headStyles: { fillColor: [40, 70, 150] },
      });
      y = doc.lastAutoTable!.finalY + 4;

      autoTable(doc, {
        startY: y,
        head: [["Forma de Pagamento", "Valor (R$)"]],
        body: [[pagamento, formatCurrency(valorPago)]],
        theme: 'grid',
        styles: { fontSize: 9 },
        headStyles: { fillColor: [40, 70, 150] },
      });
      y = doc.lastAutoTable!.finalY + 6;

      if (y > 270) {
        doc.addPage();
        y = 20;
      }

      doc.text(`Subtotal deste pedido: ${formatCurrency(totalPedido)}`, pageWidth - 15, y, { align: "right" });
      y += 10;
    });

    if (y > 270) {
      doc.addPage();
      y = 20;
    }

    if(y != 20)
      y -= 3;
    doc.setFontSize(11);
    doc.text(`TOTAL DO CLIENTE: ${formatCurrency(totalCliente)}`, pageWidth - 15, y, { align: "right" });
  });

  y += 15;
  doc.setFontSize(13);
  doc.text(`TOTAL GERAL: ${formatCurrency(totalGeral)}`, 10, y);

  await gerarGraficoPizza(dados, doc);

  const blob = doc.output('blob');
  const url = URL.createObjectURL(blob);
  window.open(url);
};
